import { cn } from '@/lib/utils';
import { useWidgetSettings } from '@/context/PropertyContext';
import { ExperienceCard } from './ExperienceCard';
import { ExperienceCarousel } from './ExperienceCarousel';
import { MapPin, Search, Sparkles } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { Experience } from '@/lib/api';

interface ExperienceListProps {
  experiences: Experience[];
  isLoading?: boolean;
  className?: string;
  /** Custom message to show when no experiences are found */
  emptyMessage?: string;
  /** Custom description for empty state */
  emptyDescription?: string;
}

/**
 * ExperienceList - Premium grid/list/carousel display of experiences
 *
 * Features:
 * - Responsive grid with elegant spacing
 * - Staggered fade-in animations on load
 * - Mobile carousel option (configurable)
 * - Luxurious loading skeletons
 * - Elegant empty state
 * - Full WCAG 2.1 AA compliance
 */
export function ExperienceList({
  experiences,
  isLoading,
  className,
  emptyMessage = "No experiences found",
  emptyDescription = "We couldn't find any experiences matching your criteria. Try adjusting your search or filters.",
}: ExperienceListProps) {
  const settings = useWidgetSettings();
  const isMobile = useMediaQuery('(max-width: 640px)');

  // Determine grid columns based on settings - with generous gaps
  const gridCols =
    {
      1: 'grid-cols-1',
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
    }[settings.columns] || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  // Loading state with accessible skeletons
  if (isLoading) {
    const skeletonCount = settings.columns === 1 ? 4 : 6;
    return (
      <div 
        className={cn('grid gap-6 lg:gap-8', gridCols, className)}
        aria-busy="true"
        aria-label="Loading experiences"
        role="status"
      >
        <span className="sr-only">Loading experiences, please wait...</span>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <ExperienceCardSkeleton
            key={i}
            showImage={settings.showImages}
            showPrice={settings.showPrices}
            animationDelay={i * 100}
          />
        ))}
      </div>
    );
  }

  // Empty state - Elegant and inviting
  if (experiences.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center py-20 px-4"
        role="status"
        aria-label={emptyMessage}
      >
        {/* Elegant icon container */}
        <div 
          className="relative mb-6"
          aria-hidden="true"
        >
          <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-muted to-muted/60 flex items-center justify-center shadow-soft">
            <Search className="h-8 w-8 text-muted-foreground/40" strokeWidth={1.5} />
          </div>
          {/* Decorative sparkle */}
          <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="h-3 w-3 text-primary/60" />
          </div>
        </div>
        
        <h3 className="font-serif text-xl font-medium text-foreground mb-2">
          {emptyMessage}
        </h3>
        <p className="text-sm text-muted-foreground text-center max-w-md leading-relaxed">
          {emptyDescription}
        </p>
      </div>
    );
  }

  // Mobile carousel mode
  if (isMobile && settings.mobileCarousel) {
    return (
      <ExperienceCarousel 
        experiences={experiences} 
        className={className}
      />
    );
  }

  // List layout
  if (settings.layout === 'list') {
    return (
      <div 
        className={cn('space-y-5', className)}
        role="list"
        aria-label={`${experiences.length} experiences available`}
      >
        {experiences.map((experience, index) => (
          <div key={experience.id} role="listitem">
            <ExperienceCard
              experience={experience}
              layout="horizontal"
              animationDelay={index * 75}
            />
          </div>
        ))}
      </div>
    );
  }

  // Grid layout (default) - with staggered animations
  return (
    <div 
      className={cn('grid gap-6 lg:gap-8', gridCols, className)}
      role="list"
      aria-label={`${experiences.length} experiences available`}
    >
      {experiences.map((experience, index) => (
        <div key={experience.id} role="listitem">
          <ExperienceCard
            experience={experience}
            layout="vertical"
            animationDelay={index * 75}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Premium loading skeleton for experience card
 * Features elegant shimmer animation
 */
function ExperienceCardSkeleton({
  showImage = true,
  showPrice = true,
  animationDelay = 0,
}: {
  showImage?: boolean;
  showPrice?: boolean;
  animationDelay?: number;
}) {
  return (
    <div 
      className="overflow-hidden rounded-2xl border border-border/30 bg-card shadow-luxury animate-fade-in"
      style={{ animationDelay: `${animationDelay}ms` }}
      aria-hidden="true"
    >
      {/* Image skeleton */}
      {showImage && (
        <div className="aspect-[4/3] skeleton-shine flex items-center justify-center relative overflow-hidden">
          <MapPin className="h-10 w-10 text-muted-foreground/15" strokeWidth={1} />
          {/* Gradient overlay to match real cards */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
        </div>
      )}

      {/* Content skeleton */}
      <div className="p-5 space-y-4">
        {/* Category skeleton */}
        <div className="h-4 w-24 rounded-full skeleton-shine" />

        {/* Title skeleton */}
        <div className="space-y-2">
          <div className="h-5 w-4/5 rounded-lg skeleton-shine" />
          <div className="h-5 w-2/5 rounded-lg skeleton-shine" />
        </div>

        {/* Description skeleton */}
        <div className="space-y-2">
          <div className="h-4 w-full rounded skeleton-shine" />
          <div className="h-4 w-3/4 rounded skeleton-shine" />
        </div>

        {/* Meta skeleton */}
        <div className="flex gap-4 pt-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg skeleton-shine" />
            <div className="h-4 w-16 rounded skeleton-shine" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg skeleton-shine" />
            <div className="h-4 w-20 rounded skeleton-shine" />
          </div>
        </div>

        {/* Price skeleton */}
        {showPrice && (
          <div className="pt-4 border-t border-border/30 flex justify-between items-center">
            <div className="h-3 w-12 rounded skeleton-shine" />
            <div className="h-7 w-24 rounded-lg skeleton-shine" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Horizontal skeleton for list layout
 */
export function ExperienceCardSkeletonHorizontal({
  showImage = true,
  showPrice = true,
}: {
  showImage?: boolean;
  showPrice?: boolean;
}) {
  return (
    <div 
      className="flex overflow-hidden rounded-2xl border border-border/30 bg-card shadow-luxury"
      aria-hidden="true"
    >
      {/* Image skeleton */}
      {showImage && (
        <div className="w-52 sm:w-72 flex-shrink-0 skeleton-shine flex items-center justify-center">
          <MapPin className="h-8 w-8 text-muted-foreground/15" strokeWidth={1} />
        </div>
      )}

      {/* Content skeleton */}
      <div className="flex-1 p-5 flex flex-col justify-between">
        <div className="space-y-3">
          <div className="h-4 w-20 rounded-full skeleton-shine" />
          <div className="h-6 w-3/4 rounded-lg skeleton-shine" />
          <div className="space-y-2">
            <div className="h-4 w-full rounded skeleton-shine" />
            <div className="h-4 w-2/3 rounded skeleton-shine" />
          </div>
        </div>

        <div className="flex justify-between items-end mt-4">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg skeleton-shine" />
              <div className="h-4 w-14 rounded skeleton-shine" />
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg skeleton-shine" />
              <div className="h-4 w-12 rounded skeleton-shine" />
            </div>
          </div>
          {showPrice && <div className="h-7 w-24 rounded-lg skeleton-shine" />}
        </div>
      </div>
    </div>
  );
}
